import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthError, scorePassword } from '@yuan-math/auth';
import { Button, Card } from '@yuan-math/ui';
import { PasswordField } from '../components/PasswordField.tsx';
import { useAuth } from '../state/AuthContext.tsx';
import { arrivalHref, linkCredential, resolveAuthLink } from '../state/authLink.ts';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * Setting a new password from an emailed link.
 *
 * The old password is never asked for — the person resetting is precisely the
 * person who does not have it. Authorisation comes from the code in the link,
 * which is single-use and short-lived.
 */
export function ResetPasswordPage(): JSX.Element {
  const { resetPassword, configured } = useAuth();
  const navigate = useNavigate();

  // Every shape a recovery link arrives in — `token_hash`, a PKCE `code`, a
  // token in the hash, or an error in either place — read from the URL the page
  // was opened with, before the client could strip it. See `authLink.ts`.
  const hasCredential = linkCredential(arrivalHref) !== 'none';

  /*
   * The recovery link establishes a session, which is what authorises the
   * password change. The client consumes it on initialisation, so this waits
   * for that to settle rather than exchanging the code a second time.
   */
  const [linkReady, setLinkReady] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasCredential || !configured) return;
    let cancelled = false;

    void resolveAuthLink(arrivalHref).then((outcome) => {
      if (cancelled) return;
      if (outcome.status === 'signed-in') {
        setLinkReady(true);
        window.history.replaceState({}, '', '/reset');
        return;
      }
      setLinkError(outcome.status === 'failed' ? outcome.message : 'That reset link could not be used.');
    });

    return () => {
      cancelled = true;
    };
  }, [hasCredential, configured]);

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<AuthError | string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const strength = password === '' ? null : scorePassword(password);
  const mismatch = confirmation !== '' && confirmation !== password;

  if (!configured) return <AccountsUnavailable />;

  if (!hasCredential) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">This page needs a reset link</h1>
        <p className="page__lead">
          Follow the link in the email we sent you. Links last one hour and work once.
        </p>
        <div className="button-row">
          <Link to="/forgot" className="btn btn--primary">Send a new link</Link>
          <Link to="/signin" className="btn btn--secondary">Back to sign in</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Your password is set.</h1>
        <p className="page__lead">You are signed in, and your progress is exactly where you left it.</p>
        <div className="button-row">
          <Button size="lg" onClick={() => navigate('/home')}>Continue learning</Button>
        </div>
      </div>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      // The session the link established is the authorisation, and the
      // credential itself is already spent — which is why none is passed.
      await resetPassword('', password);
      setDone(true);
    } catch (cause) {
      setError(cause instanceof AuthError ? cause : 'Could not set that password just now.');
    } finally {
      setBusy(false);
    }
  };

  const message = error instanceof AuthError ? error.message : error;
  const expired = error instanceof AuthError && (error.code === 'token-expired' || error.code === 'invalid-token');

  if (linkError) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">That link did not work</h1>
        <p className="page__lead">{linkError}</p>
        <div className="button-row">
          <Link to="/forgot" className="btn btn--primary">Send a new link</Link>
          <Link to="/signin" className="btn btn--secondary">Back to sign in</Link>
        </div>
      </div>
    );
  }

  if (!linkReady) {
    return (
      <div className="page page--narrow page--centred">
        <h1 className="page__title">Checking your link…</h1>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Choose a new password</h1>
      <p className="page__lead">
        We do not ask for your old one — that is the whole point of resetting.
      </p>

      <form className="start-form" onSubmit={submit} noValidate>
        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          autoFocus
        >
          {strength ? (
            <>
              <span className="strength" aria-live="polite">
                <span className={`strength__bar strength__bar--${strength.score}`} />
                <span className="strength__label">
                  {['Too weak', 'Weak', 'Reasonable', 'Strong', 'Very strong'][strength.score]}
                </span>
              </span>
              {strength.suggestions[0] ? <span className="field__hint">{strength.suggestions[0]}</span> : null}
            </>
          ) : (
            <span className="field__hint">
              At least 10 characters. A short phrase is stronger and easier to remember than a jumble.
            </span>
          )}
        </PasswordField>

        <PasswordField
          label="Type it again"
          value={confirmation}
          onChange={setConfirmation}
          autoComplete="new-password"
        >
          {mismatch ? <span className="field__error">The two passwords do not match.</span> : null}
        </PasswordField>

        {message ? (
          <p className="form-error" role="alert">
            {message}
            {expired ? (
              <>
                {' '}
                <Link to="/forgot">Send a new link</Link>.
              </>
            ) : null}
          </p>
        ) : null}

        <Button type="submit" size="lg" disabled={busy || password === '' || mismatch || !strength?.acceptable}>
          {busy ? 'Setting your password…' : 'Set password'}
        </Button>
      </form>

      <Card className="reset-note">
        <p>
          Setting a new password signs you in on this device. Any other device stays signed in until its session
          expires.
        </p>
      </Card>
    </div>
  );
}
